import { decodeJWT } from '@aws-amplify/core';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

// Cognito authorization codes are UUIDs. Stripe Connect sends `?code=ac_...` back to the
// same landing page, so the shape is what keeps the two flows apart.
const COGNITO_CODE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const stripOAuthParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState({}, '', url.toString());
};

/**
 * Completes a Cognito hosted-UI sign-in that was started outside this app.
 *
 * Partner platforms (e.g. DRG/SilverFish) send users to the app with an authorization
 * code already in hand. Amplify v6 only finishes flows it started itself — it requires a
 * `state` matching one it stored during `signInWithRedirect` — so those callbacks are
 * ignored and the user lands on the login page. This exchanges the code for tokens and
 * seeds Amplify's token store so `fetchAuthSession` and request signing work as usual.
 *
 * Callbacks carrying a `state` are left alone: Amplify's own listener handles those.
 *
 * @returns {Promise<boolean>} True when a session was established from the URL.
 * @example
 * await completeExternalSsoSignIn();
 * const user = await getCurrentUserInfo();
 */
export const completeExternalSsoSignIn = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');

  if (!code || params.get('state') || !COGNITO_CODE.test(code)) return false;

  const { userPoolClientId, loginWith } = Amplify.getConfig()?.Auth?.Cognito || {};
  const { domain, redirectSignIn } = loginWith?.oauth || {};

  if (!userPoolClientId || !domain || !redirectSignIn?.length) return false;

  try {
    const response = await fetch(`https://${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: userPoolClientId,
        code,
        // Must byte-match the redirect_uri the partner used, or Cognito returns invalid_grant.
        redirect_uri: redirectSignIn[0],
      }),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed (${response.status}): ${await response.text()}`);
    }

    const { access_token: accessToken, id_token: rawIdToken, refresh_token: refreshToken } = await response.json();
    const idToken = decodeJWT(rawIdToken);

    await cognitoUserPoolsTokenProvider.tokenOrchestrator.setTokens({
      tokens: {
        accessToken: decodeJWT(accessToken),
        idToken,
        refreshToken,
        clockDrift: 0,
        username: idToken.payload['cognito:username'],
      },
    });

    return true;
  } catch (error) {
    console.error('completeExternalSsoSignIn', error);
    return false;
  } finally {
    stripOAuthParams();
  }
};
